/**
 * Comprehensive currency data mapping
 * Combines currency codes with their country codes, full currency names, and symbols
 * Based on ISO 4217 currency codes and ISO 3166-1 alpha-2 country codes
 */

export interface CurrencyData {
  countryCode: string;
  name: string;
  symbol: string;
}

export const CURRENCY_DATA = {
  AED: { countryCode: 'AE', name: 'United Arab Emirates Dirham', symbol: 'د.إ' },
  AFN: { countryCode: 'AF', name: 'Afghan Afghani', symbol: '؋' },
  ALL: { countryCode: 'AL', name: 'Albanian Lek', symbol: 'L' },
  AMD: { countryCode: 'AM', name: 'Armenian Dram', symbol: '֏' },
  ANG: { countryCode: 'CW', name: 'Netherlands Antillean Guilder', symbol: 'ƒ' },
  AOA: { countryCode: 'AO', name: 'Angolan Kwanza', symbol: 'Kz' },
  ARS: { countryCode: 'AR', name: 'Argentine Peso', symbol: '$' },
  AUD: { countryCode: 'AU', name: 'Australian Dollar', symbol: 'A$' },
  AWG: { countryCode: 'AW', name: 'Aruban Florin', symbol: 'ƒ' },
  AZN: { countryCode: 'AZ', name: 'Azerbaijani Manat', symbol: '₼' },
  BAM: { countryCode: 'BA', name: 'Bosnia and Herzegovina Convertible Mark', symbol: 'KM' },
  BBD: { countryCode: 'BB', name: 'Barbadian Dollar', symbol: '$' },
  BDT: { countryCode: 'BD', name: 'Bangladeshi Taka', symbol: '৳' },
  BGN: { countryCode: 'BG', name: 'Bulgarian Lev', symbol: 'лв' },
  BHD: { countryCode: 'BH', name: 'Bahraini Dinar', symbol: '.د.ب' },
  BIF: { countryCode: 'BI', name: 'Burundian Franc', symbol: 'Fr' },
  BMD: { countryCode: 'BM', name: 'Bermudian Dollar', symbol: '$' },
  BND: { countryCode: 'BN', name: 'Brunei Dollar', symbol: '$' },
  BOB: { countryCode: 'BO', name: 'Bolivian Boliviano', symbol: 'Bs.' },
  BRL: { countryCode: 'BR', name: 'Brazilian Real', symbol: 'R$' },
  BSD: { countryCode: 'BS', name: 'Bahamian Dollar', symbol: '$' },
  BTN: { countryCode: 'BT', name: 'Bhutanese Ngultrum', symbol: 'Nu.' },
  BWP: { countryCode: 'BW', name: 'Botswana Pula', symbol: 'P' },
  BYN: { countryCode: 'BY', name: 'Belarusian Ruble', symbol: 'Br' },
  BZD: { countryCode: 'BZ', name: 'Belize Dollar', symbol: '$' },
  CAD: { countryCode: 'CA', name: 'Canadian Dollar', symbol: 'C$' },
  CDF: { countryCode: 'CD', name: 'Congolese Franc', symbol: 'Fr' },
  CHF: { countryCode: 'CH', name: 'Swiss Franc', symbol: 'Fr' },
  CLP: { countryCode: 'CL', name: 'Chilean Peso', symbol: '$' },
  CNY: { countryCode: 'CN', name: 'Chinese Yuan', symbol: '¥' },
  COP: { countryCode: 'CO', name: 'Colombian Peso', symbol: '$' },
  CRC: { countryCode: 'CR', name: 'Costa Rican Colón', symbol: '₡' },
  CUC: { countryCode: 'CU', name: 'Cuban Convertible Peso', symbol: '$' },
  CUP: { countryCode: 'CU', name: 'Cuban Peso', symbol: '$' },
  CVE: { countryCode: 'CV', name: 'Cape Verdean Escudo', symbol: '$' },
  CZK: { countryCode: 'CZ', name: 'Czech Koruna', symbol: 'Kč' },
  DJF: { countryCode: 'DJ', name: 'Djiboutian Franc', symbol: 'Fr' },
  DKK: { countryCode: 'DK', name: 'Danish Krone', symbol: 'kr' },
  DOP: { countryCode: 'DO', name: 'Dominican Peso', symbol: '$' },
  DZD: { countryCode: 'DZ', name: 'Algerian Dinar', symbol: 'د.ج' },
  EGP: { countryCode: 'EG', name: 'Egyptian Pound', symbol: 'E£' },
  ERN: { countryCode: 'ER', name: 'Eritrean Nakfa', symbol: 'Nfk' },
  ETB: { countryCode: 'ET', name: 'Ethiopian Birr', symbol: 'Br' },
  EUR: { countryCode: 'EU', name: 'Euro', symbol: '€' },
  FJD: { countryCode: 'FJ', name: 'Fijian Dollar', symbol: '$' },
  FKP: { countryCode: 'FK', name: 'Falkland Islands Pound', symbol: '£' },
  FOK: { countryCode: 'FO', name: 'Faroese Króna', symbol: 'kr' },
  GBP: { countryCode: 'GB', name: 'British Pound Sterling', symbol: '£' },
  GEL: { countryCode: 'GE', name: 'Georgian Lari', symbol: '₾' },
  GGP: { countryCode: 'GG', name: 'Guernsey Pound', symbol: '£' },
  GHS: { countryCode: 'GH', name: 'Ghanaian Cedi', symbol: '₵' },
  GIP: { countryCode: 'GI', name: 'Gibraltar Pound', symbol: '£' },
  GMD: { countryCode: 'GM', name: 'Gambian Dalasi', symbol: 'D' },
  GNF: { countryCode: 'GN', name: 'Guinean Franc', symbol: 'Fr' },
  GTQ: { countryCode: 'GT', name: 'Guatemalan Quetzal', symbol: 'Q' },
  GYD: { countryCode: 'GY', name: 'Guyanese Dollar', symbol: '$' },
  HKD: { countryCode: 'HK', name: 'Hong Kong Dollar', symbol: 'HK$' },
  HNL: { countryCode: 'HN', name: 'Honduran Lempira', symbol: 'L' },
  HRK: { countryCode: 'HR', name: 'Croatian Kuna', symbol: 'kn' },
  HTG: { countryCode: 'HT', name: 'Haitian Gourde', symbol: 'G' },
  HUF: { countryCode: 'HU', name: 'Hungarian Forint', symbol: 'Ft' },
  IDR: { countryCode: 'ID', name: 'Indonesian Rupiah', symbol: 'Rp' },
  ILS: { countryCode: 'IL', name: 'Israeli New Shekel', symbol: '₪' },
  IMP: { countryCode: 'IM', name: 'Isle of Man Pound', symbol: '£' },
  INR: { countryCode: 'IN', name: 'Indian Rupee', symbol: '₹' },
  IQD: { countryCode: 'IQ', name: 'Iraqi Dinar', symbol: 'ع.د' },
  IRR: { countryCode: 'IR', name: 'Iranian Rial', symbol: '﷼' },
  ISK: { countryCode: 'IS', name: 'Icelandic Króna', symbol: 'kr' },
  JEP: { countryCode: 'JE', name: 'Jersey Pound', symbol: '£' },
  JMD: { countryCode: 'JM', name: 'Jamaican Dollar', symbol: '$' },
  JOD: { countryCode: 'JO', name: 'Jordanian Dinar', symbol: 'د.ا' },
  JPY: { countryCode: 'JP', name: 'Japanese Yen', symbol: '¥' },
  KES: { countryCode: 'KE', name: 'Kenyan Shilling', symbol: 'Sh' },
  KGS: { countryCode: 'KG', name: 'Kyrgyzstani Som', symbol: 'с' },
  KHR: { countryCode: 'KH', name: 'Cambodian Riel', symbol: '៛' },
  KID: { countryCode: 'KI', name: 'Kiribati Dollar', symbol: '$' },
  KMF: { countryCode: 'KM', name: 'Comorian Franc', symbol: 'Fr' },
  KRW: { countryCode: 'KR', name: 'South Korean Won', symbol: '₩' },
  KWD: { countryCode: 'KW', name: 'Kuwaiti Dinar', symbol: 'د.ك' },
  KYD: { countryCode: 'KY', name: 'Cayman Islands Dollar', symbol: '$' },
  KZT: { countryCode: 'KZ', name: 'Kazakhstani Tenge', symbol: '₸' },
  LAK: { countryCode: 'LA', name: 'Lao Kip', symbol: '₭' },
  LBP: { countryCode: 'LB', name: 'Lebanese Pound', symbol: 'ل.ل' },
  LKR: { countryCode: 'LK', name: 'Sri Lankan Rupee', symbol: 'Rs' },
  LRD: { countryCode: 'LR', name: 'Liberian Dollar', symbol: '$' },
  LSL: { countryCode: 'LS', name: 'Lesotho Loti', symbol: 'L' },
  LYD: { countryCode: 'LY', name: 'Libyan Dinar', symbol: 'ل.د' },
  MAD: { countryCode: 'MA', name: 'Moroccan Dirham', symbol: 'د.م.' },
  MDL: { countryCode: 'MD', name: 'Moldovan Leu', symbol: 'L' },
  MGA: { countryCode: 'MG', name: 'Malagasy Ariary', symbol: 'Ar' },
  MKD: { countryCode: 'MK', name: 'Macedonian Denar', symbol: 'ден' },
  MMK: { countryCode: 'MM', name: 'Myanmar Kyat', symbol: 'K' },
  MNT: { countryCode: 'MN', name: 'Mongolian Tögrög', symbol: '₮' },
  MOP: { countryCode: 'MO', name: 'Macanese Pataca', symbol: 'P' },
  MRU: { countryCode: 'MR', name: 'Mauritanian Ouguiya', symbol: 'UM' },
  MUR: { countryCode: 'MU', name: 'Mauritian Rupee', symbol: '₨' },
  MVR: { countryCode: 'MV', name: 'Maldivian Rufiyaa', symbol: 'ރ.' },
  MWK: { countryCode: 'MW', name: 'Malawian Kwacha', symbol: 'MK' },
  MXN: { countryCode: 'MX', name: 'Mexican Peso', symbol: '$' },
  MYR: { countryCode: 'MY', name: 'Malaysian Ringgit', symbol: 'RM' },
  MZN: { countryCode: 'MZ', name: 'Mozambican Metical', symbol: 'MT' },
  NAD: { countryCode: 'NA', name: 'Namibian Dollar', symbol: '$' },
  NGN: { countryCode: 'NG', name: 'Nigerian Naira', symbol: '₦' },
  NIO: { countryCode: 'NI', name: 'Nicaraguan Córdoba', symbol: 'C$' },
  NOK: { countryCode: 'NO', name: 'Norwegian Krone', symbol: 'kr' },
  NPR: { countryCode: 'NP', name: 'Nepalese Rupee', symbol: '₨' },
  NZD: { countryCode: 'NZ', name: 'New Zealand Dollar', symbol: 'NZ$' },
  OMR: { countryCode: 'OM', name: 'Omani Rial', symbol: 'ر.ع.' },
  PAB: { countryCode: 'PA', name: 'Panamanian Balboa', symbol: 'B/.' },
  PEN: { countryCode: 'PE', name: 'Peruvian Sol', symbol: 'S/' },
  PGK: { countryCode: 'PG', name: 'Papua New Guinean Kina', symbol: 'K' },
  PHP: { countryCode: 'PH', name: 'Philippine Peso', symbol: '₱' },
  PKR: { countryCode: 'PK', name: 'Pakistani Rupee', symbol: '₨' },
  PLN: { countryCode: 'PL', name: 'Polish Złoty', symbol: 'zł' },
  PYG: { countryCode: 'PY', name: 'Paraguayan Guaraní', symbol: '₲' },
  QAR: { countryCode: 'QA', name: 'Qatari Riyal', symbol: 'ر.ق' },
  RON: { countryCode: 'RO', name: 'Romanian Leu', symbol: 'lei' },
  RSD: { countryCode: 'RS', name: 'Serbian Dinar', symbol: 'дин.' },
  RUB: { countryCode: 'RU', name: 'Russian Ruble', symbol: '₽' },
  RWF: { countryCode: 'RW', name: 'Rwandan Franc', symbol: 'Fr' },
  SAR: { countryCode: 'SA', name: 'Saudi Riyal', symbol: 'ر.س' },
  SBD: { countryCode: 'SB', name: 'Solomon Islands Dollar', symbol: '$' },
  SCR: { countryCode: 'SC', name: 'Seychellois Rupee', symbol: '₨' },
  SDG: { countryCode: 'SD', name: 'Sudanese Pound', symbol: 'ج.س.' },
  SEK: { countryCode: 'SE', name: 'Swedish Krona', symbol: 'kr' },
  SGD: { countryCode: 'SG', name: 'Singapore Dollar', symbol: 'S$' },
  SHP: { countryCode: 'SH', name: 'Saint Helena Pound', symbol: '£' },
  SLE: { countryCode: 'SL', name: 'Sierra Leonean Leone', symbol: 'Le' },
  SOS: { countryCode: 'SO', name: 'Somali Shilling', symbol: 'Sh' },
  SRD: { countryCode: 'SR', name: 'Surinamese Dollar', symbol: '$' },
  SSP: { countryCode: 'SS', name: 'South Sudanese Pound', symbol: '£' },
  STN: { countryCode: 'ST', name: 'São Tomé and Príncipe Dobra', symbol: 'Db' },
  SYP: { countryCode: 'SY', name: 'Syrian Pound', symbol: '£' },
  SZL: { countryCode: 'SZ', name: 'Eswatini Lilangeni', symbol: 'L' },
  THB: { countryCode: 'TH', name: 'Thai Baht', symbol: '฿' },
  TJS: { countryCode: 'TJ', name: 'Tajikistani Somoni', symbol: 'ЅМ' },
  TMT: { countryCode: 'TM', name: 'Turkmenistani Manat', symbol: 'm' },
  TND: { countryCode: 'TN', name: 'Tunisian Dinar', symbol: 'د.ت' },
  TOP: { countryCode: 'TO', name: 'Tongan Paʻanga', symbol: 'T$' },
  TRY: { countryCode: 'TR', name: 'Turkish Lira', symbol: '₺' },
  TTD: { countryCode: 'TT', name: 'Trinidad and Tobago Dollar', symbol: '$' },
  TVD: { countryCode: 'TV', name: 'Tuvaluan Dollar', symbol: '$' },
  TZS: { countryCode: 'TZ', name: 'Tanzanian Shilling', symbol: 'Sh' },
  UAH: { countryCode: 'UA', name: 'Ukrainian Hryvnia', symbol: '₴' },
  UGX: { countryCode: 'UG', name: 'Ugandan Shilling', symbol: 'Sh' },
  USD: { countryCode: 'US', name: 'United States Dollar', symbol: '$' },
  UYU: { countryCode: 'UY', name: 'Uruguayan Peso', symbol: '$' },
  UZS: { countryCode: 'UZ', name: 'Uzbekistani Som', symbol: 'сўм' },
  VES: { countryCode: 'VE', name: 'Venezuelan Bolívar', symbol: 'Bs.S' },
  VND: { countryCode: 'VN', name: 'Vietnamese Đồng', symbol: '₫' },
  VUV: { countryCode: 'VU', name: 'Vanuatu Vatu', symbol: 'Vt' },
  WST: { countryCode: 'WS', name: 'Samoan Tālā', symbol: 'T' },
  XAF: { countryCode: 'CM', name: 'Central African CFA Franc', symbol: 'Fr' },
  XCD: { countryCode: 'AG', name: 'East Caribbean Dollar', symbol: '$' },
  XOF: { countryCode: 'SN', name: 'West African CFA Franc', symbol: 'Fr' },
  XPF: { countryCode: 'PF', name: 'CFP Franc', symbol: '₣' },
  YER: { countryCode: 'YE', name: 'Yemeni Rial', symbol: '﷼' },
  ZAR: { countryCode: 'ZA', name: 'South African Rand', symbol: 'R' },
  ZMW: { countryCode: 'ZM', name: 'Zambian Kwacha', symbol: 'ZK' },
  ZWL: { countryCode: 'ZW', name: 'Zimbabwean Dollar', symbol: '$' },
} as const;

export type CurrencyCode = keyof typeof CURRENCY_DATA;
export type CountryCode = (typeof CURRENCY_DATA)[CurrencyCode]['countryCode'];

/**
 * Gets the currency symbol for a given currency code.
 *
 * @param currencyCode - The ISO 4217 currency code (e.g., 'USD', 'EUR', 'GBP')
 * @param fallback - Optional fallback value if currency not found (defaults to the currency code itself)
 * @returns The currency symbol (e.g., '$', '€', '£')
 *
 * @example
 * ```typescript
 * getCurrencySign('USD'); // '$'
 * getCurrencySign('EUR'); // '€'
 * getCurrencySign('GBP'); // '£'
 * getCurrencySign('INVALID'); // 'INVALID'
 * getCurrencySign('INVALID', '?'); // '?'
 * ```
 */
export function getCurrencySign(currencyCode: string, fallback?: string): string {
  const data = CURRENCY_DATA[currencyCode as CurrencyCode];
  return data?.symbol || fallback || currencyCode;
}

/**
 * Legacy export for backward compatibility with CURRENCY_TO_COUNTRY mapping
 * @deprecated Use CURRENCY_DATA instead for access to country codes, currency names, and symbols
 */
export const CURRENCY_TO_COUNTRY = Object.entries(CURRENCY_DATA).reduce(
  (acc, [currencyCode, data]) => {
    acc[currencyCode as CurrencyCode] = data.countryCode;
    return acc;
  },
  {} as Record<CurrencyCode, string>
);
